/**
 * ComplianceDocumentModal — Upload / Edit compliance document modal
 *
 * Handles both:
 *  - Create mode: "Upload Document" → creates new document record
 *  - Edit mode: Pre-populates all fields; fileUrl is read-only
 *
 * Consumed by:
 *  - documents.tsx (library page) — create and edit mode
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentData {
  id?: string;
  documentCategory?: string;
  documentName?: string;
  documentDescription?: string | null;
  version?: string | null;
  effectiveDate?: string | null;
  reviewDate?: string | null;
  documentStatus?: string;
  fileUrl?: string | null;
  filePath?: string | null;
  regulatoryReference?: string | null;
}

interface ComplianceDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingDocument?: DocumentData | null;
  onSuccess?: (documentId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_OPTIONS = [
  { value: "policy", label: "Policy" },
  { value: "procedure", label: "Procedure" },
  { value: "template", label: "Template" },
  { value: "register", label: "Register" },
  { value: "report", label: "Report" },
  { value: "certificate", label: "Certificate" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "superseded", label: "Superseded" },
  { value: "archived", label: "Archived" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ComplianceDocumentModal({
  open,
  onOpenChange,
  editingDocument,
  onSuccess,
}: ComplianceDocumentModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!editingDocument?.id;

  // Form state
  const [documentCategory, setDocumentCategory] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [documentDescription, setDocumentDescription] = useState("");
  const [version, setVersion] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [documentStatus, setDocumentStatus] = useState("draft");
  const [fileUrl, setFileUrl] = useState("");
  const [filePath, setFilePath] = useState("");
  const [regulatoryReference, setRegulatoryReference] = useState("");

  // Reset / pre-populate form when modal opens or editingDocument changes
  useEffect(() => {
    if (open && editingDocument) {
      setDocumentCategory(editingDocument.documentCategory ?? "");
      setDocumentName(editingDocument.documentName ?? "");
      setDocumentDescription(editingDocument.documentDescription ?? "");
      setVersion(editingDocument.version ?? "");
      setEffectiveDate(editingDocument.effectiveDate ?? "");
      setReviewDate(editingDocument.reviewDate ?? "");
      setDocumentStatus(editingDocument.documentStatus ?? "draft");
      setFileUrl(editingDocument.fileUrl ?? "");
      setFilePath(editingDocument.filePath ?? "");
      setRegulatoryReference(editingDocument.regulatoryReference ?? "");
    } else if (open && !editingDocument) {
      setDocumentCategory("");
      setDocumentName("");
      setDocumentDescription("");
      setVersion("");
      setEffectiveDate("");
      setReviewDate("");
      setDocumentStatus("draft");
      setFileUrl("");
      setFilePath("");
      setRegulatoryReference("");
    }
  }, [open, editingDocument]);

  // Form validity: category and name are required
  const isValid =
    documentCategory.length > 0 && documentName.trim().length > 0;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/compliance/documents", {
        documentCategory,
        documentName: documentName.trim(),
        documentDescription: documentDescription.trim() || undefined,
        version: version.trim() || undefined,
        effectiveDate: effectiveDate || undefined,
        reviewDate: reviewDate || undefined,
        documentStatus,
        fileUrl: fileUrl.trim() || undefined,
        filePath: filePath.trim() || undefined,
        regulatoryReference: regulatoryReference.trim() || undefined,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to upload document"
        );
      }
      return response.json();
    },
    onSuccess: (data: DocumentData) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/documents"],
      });
      toast({ title: "Document added successfully" });
      handleOpenChange(false);
      onSuccess?.(data.id as string);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingDocument?.id) throw new Error("No document ID");
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/documents/${editingDocument.id}`,
        {
          documentCategory,
          documentName: documentName.trim(),
          documentDescription: documentDescription.trim() || undefined,
          version: version.trim() || undefined,
          effectiveDate: effectiveDate || undefined,
          reviewDate: reviewDate || undefined,
          documentStatus,
          filePath: filePath.trim() || undefined,
          regulatoryReference: regulatoryReference.trim() || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to update document"
        );
      }
      return response.json();
    },
    onSuccess: (data: DocumentData) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/documents"],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/documents/${data.id}`],
      });
      toast({ title: "Document updated successfully" });
      handleOpenChange(false);
      onSuccess?.(data.id as string);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="modal-compliance-document-form"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Document" : "Upload Document"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the document metadata below."
              : "Add a compliance document to the library."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Document Info */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Document Info
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Category */}
              <div className="space-y-1.5">
                <Label>
                  Category <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={documentCategory}
                  onValueChange={setDocumentCategory}
                >
                  <SelectTrigger data-testid="select-document-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={documentStatus}
                  onValueChange={setDocumentStatus}
                >
                  <SelectTrigger data-testid="select-document-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Document Name */}
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="document-name">
                  Document Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="document-name"
                  data-testid="input-document-name"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  placeholder="e.g. Anti-Money Laundering Policy"
                />
              </div>

              {/* Description */}
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="document-description">Description</Label>
                <Textarea
                  id="document-description"
                  data-testid="input-document-description"
                  value={documentDescription}
                  onChange={(e) => setDocumentDescription(e.target.value)}
                  placeholder="Brief description of this document..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Details
            </h3>

            <div className="grid grid-cols-3 gap-4">
              {/* Version */}
              <div className="space-y-1.5">
                <Label htmlFor="document-version">Version</Label>
                <Input
                  id="document-version"
                  data-testid="input-document-version"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="e.g. 2.1"
                />
              </div>

              {/* Effective Date */}
              <div className="space-y-1.5">
                <Label htmlFor="effective-date">Effective Date</Label>
                <Input
                  id="effective-date"
                  data-testid="input-effective-date"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
              </div>

              {/* Review Date */}
              <div className="space-y-1.5">
                <Label htmlFor="review-date">Review Date</Label>
                <Input
                  id="review-date"
                  data-testid="input-review-date"
                  type="date"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* File */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              File
            </h3>

            <div className="grid grid-cols-1 gap-4">
              {/* File URL */}
              <div className="space-y-1.5">
                <Label htmlFor="file-url">File URL</Label>
                {isEditMode ? (
                  <Input
                    id="file-url"
                    data-testid="input-file-url"
                    value={fileUrl}
                    readOnly
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                ) : (
                  <Input
                    id="file-url"
                    data-testid="input-file-url"
                    value={fileUrl}
                    onChange={(e) => setFileUrl(e.target.value)}
                    placeholder="https://sharepoint.com/sites/..."
                  />
                )}
              </div>

              {/* File Path */}
              <div className="space-y-1.5">
                <Label htmlFor="file-path">File Path</Label>
                <Input
                  id="file-path"
                  data-testid="input-file-path"
                  value={filePath}
                  onChange={(e) => setFilePath(e.target.value)}
                  placeholder="Compliance/Policies/AML-Policy-v2.pdf"
                />
              </div>
            </div>
          </div>

          {/* Regulatory */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Regulatory
            </h3>

            <div className="space-y-1.5">
              <Label htmlFor="regulatory-reference">
                Regulatory Reference
              </Label>
              <Textarea
                id="regulatory-reference"
                data-testid="input-regulatory-reference"
                value={regulatoryReference}
                onChange={(e) => setRegulatoryReference(e.target.value)}
                placeholder="e.g. AML/CTF Act 2006, s.36 — Customer identification"
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending
              ? "Saving..."
              : isEditMode
              ? "Save Changes"
              : "Upload Document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
